// Deterministisches Swimlane-Layout.
// Eingabe: Knoten und Kanten eines Prozesses plus Reihenfolge der Bahnen.
// Ausgabe: Koordinaten für Bahnen, Knoten und orthogonal geführte Kanten.
// Manuelle Eingriffe werden nur als Hinweise am Knoten gespeichert
// (lane, colHint = Mindestspalte, order = Position im Zellstapel).

export const DIM = {
  W: 212,            // Knotenbreite
  H: 84,             // Knotenhöhe
  rowGap: 22,        // Abstand gestapelter Knoten in einer Zelle
  lanePad: 14,       // Innenabstand unten in einer Bahn
  laneHeader: 140,   // Breite des Bahnkopfs links
  channelBase: 50,   // Mindestbreite eines Kanals zwischen zwei Spalten
  trackGap: 9,       // Abstand paralleler Kantenspuren
  corridorBase: 18,  // Mindesthöhe des Korridors oben in jeder Bahn
  edgeChannel: 22,   // Kanal links von Spalte 0, wenn ungenutzt
  marginRight: 36,
};

const BIG = 1e9;

function cmpNodes(a, b) {
  const oa = a.order ?? BIG, ob = b.order ?? BIG;
  if (oa !== ob) return oa - ob;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * @param {{nodes: object[], edges: object[], laneOrder: string[]}} input
 */
export function labelWidth(text) {
  const t = String(text || '');
  if (!t) return 0;
  return Math.min(190, Math.min(t.length, 28) * 10.5 * 0.56 + 16);
}

export function layoutProcess({ nodes, edges, laneOrder, labelText = (e) => e.label }) {
  const D = DIM;
  const sorted = [...nodes].sort(cmpNodes);
  const byId = new Map(sorted.map((n) => [n.id, n]));

  // Bahnen: vorgegebene Reihenfolge, dazu benutzte, aber nicht gelistete Bahnen
  const lanes = [];
  for (const l of laneOrder || []) if (!lanes.includes(l)) lanes.push(l);
  for (const n of sorted) if (!lanes.includes(n.lane)) lanes.push(n.lane);
  const laneIdx = new Map(lanes.map((l, i) => [l, i]));

  const validEdges = edges
    .filter((e) => byId.has(e.from) && byId.has(e.to) && e.from !== e.to)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const out = new Map(sorted.map((n) => [n.id, []]));
  const indeg = new Map(sorted.map((n) => [n.id, 0]));
  for (const e of validEdges) {
    out.get(e.from).push(e);
    indeg.set(e.to, indeg.get(e.to) + 1);
  }

  // 1. Rückkanten per Tiefensuche erkennen (Startknoten zuerst)
  const state = new Map(); // 1 = in Bearbeitung, 2 = fertig
  const back = new Set();
  const roots = [
    ...sorted.filter((n) => indeg.get(n.id) === 0),
    ...sorted.filter((n) => indeg.get(n.id) !== 0),
  ];
  for (const r of roots) {
    if (state.has(r.id)) continue;
    const stack = [[r.id, 0]];
    state.set(r.id, 1);
    while (stack.length) {
      const top = stack[stack.length - 1];
      const list = out.get(top[0]);
      if (top[1] < list.length) {
        const e = list[top[1]++];
        const s = state.get(e.to);
        if (s === 1) back.add(e.id);
        else if (!s) {
          state.set(e.to, 1);
          stack.push([e.to, 0]);
        }
      } else {
        state.set(top[0], 2);
        stack.pop();
      }
    }
  }

  // 2. Spalten: längster Pfad über Vorwärtskanten, Mindestspalte colHint
  const fwdIn = new Map(sorted.map((n) => [n.id, []]));
  const fwdOutCount = new Map(sorted.map((n) => [n.id, 0]));
  for (const e of validEdges) {
    if (back.has(e.id)) continue;
    fwdIn.get(e.to).push(e);
    fwdOutCount.set(e.from, fwdOutCount.get(e.from) + 1);
  }
  const rank = new Map();
  const remaining = new Map(sorted.map((n) => [n.id, fwdIn.get(n.id).length]));
  const queue = sorted.filter((n) => remaining.get(n.id) === 0).map((n) => n.id);
  const topo = [];
  while (queue.length) {
    const id = queue.shift();
    topo.push(id);
    const n = byId.get(id);
    let r = Math.max(0, Math.floor(n.colHint || 0));
    for (const e of fwdIn.get(id)) r = Math.max(r, rank.get(e.from) + 1);
    rank.set(id, r);
    for (const e of out.get(id)) {
      if (back.has(e.id)) continue;
      remaining.set(e.to, remaining.get(e.to) - 1);
      if (remaining.get(e.to) === 0) queue.push(e.to);
    }
  }
  const maxRank = Math.max(0, ...rank.values());

  // 3. Zellen (Bahn × Spalte) mit gestapelten Knoten
  const cells = new Map();
  const rowOf = new Map();
  const ordered = [...sorted].sort((a, b) => rank.get(a.id) - rank.get(b.id) || cmpNodes(a, b));
  const groups = new Map();
  for (const n of ordered) {
    const key = n.lane + '|' + rank.get(n.id);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(n);
  }
  for (let r = 0; r <= maxRank; r++) {
    for (const lane of lanes) {
      const key = lane + '|' + r;
      const list = groups.get(key);
      if (!list) continue;
      const bary = (n) => {
        const preds = fwdIn.get(n.id).map((e) => e.from).filter((p) => rowOf.has(p));
        if (!preds.length) return BIG;
        return preds.reduce((s, p) => s + laneIdx.get(byId.get(p).lane) * 1000 + rowOf.get(p), 0) / preds.length;
      };
      list.sort((a, b) => {
        const oa = a.order ?? BIG, ob = b.order ?? BIG;
        if (oa !== ob) return oa - ob;
        const ba = bary(a), bb = bary(b);
        if (ba !== bb) return ba - bb;
        return a.id < b.id ? -1 : 1;
      });
      list.forEach((n, i) => rowOf.set(n.id, i));
      cells.set(key, list.map((n) => n.id));
    }
  }
  const laneRows = lanes.map((l) => {
    let m = 1;
    for (let r = 0; r <= maxRank; r++) m = Math.max(m, (cells.get(l + '|' + r) || []).length);
    return m;
  });

  // 4. Kantenrouten logisch bestimmen
  // direkt: Zielspalte = Quellspalte + 1 → eine Senkrechte im Kanal rechts der Quelle
  // Korridor: Senkrechte rechts der Quelle → waagerecht im Korridor der Zielbahn → Senkrechte links vom Ziel
  const routes = validEdges.map((e) => {
    const rs = rank.get(e.from), rt = rank.get(e.to);
    const direct = !back.has(e.id) && rt === rs + 1;
    return { e, rs, rt, direct, back: back.has(e.id), laneT: byId.get(e.to).lane };
  });

  // Vorläufige y-Werte (Korridore in Grundhöhe) für die Spurvergabe
  const prelim = computeY(lanes, laneRows, lanes.map(() => D.corridorBase));
  const yNode = (id, Y) => {
    const n = byId.get(id);
    const li = laneIdx.get(n.lane);
    return Y.laneTop[li] + Y.corr[li] + rowOf.get(id) * (D.H + D.rowGap) + D.H / 2;
  };
  const yCorr = (lane, Y) => Y.laneTop[laneIdx.get(lane)] + Y.corr[laneIdx.get(lane)] / 2;

  // Senkrechte Segmente je Kanal sammeln; gleiche Quelle bzw. gleiches Ziel teilen sich eine Spur
  const vGroups = new Map(); // channel -> Map(groupKey -> {min,max})
  const addV = (ch, key, a, b) => {
    if (!vGroups.has(ch)) vGroups.set(ch, new Map());
    const g = vGroups.get(ch);
    const lo = Math.min(a, b), hi = Math.max(a, b);
    const cur = g.get(key);
    if (cur) { cur.min = Math.min(cur.min, lo); cur.max = Math.max(cur.max, hi); }
    else g.set(key, { min: lo, max: hi });
  };
  const hGroups = new Map(); // lane -> Map(groupKey -> {min,max})
  for (const r of routes) {
    const ys = yNode(r.e.from, prelim), yt = yNode(r.e.to, prelim);
    if (r.direct) {
      r.vKey1 = 's:' + r.e.from;
      addV(r.rs, r.vKey1, ys, yt);
    } else {
      const yc = yCorr(r.laneT, prelim);
      r.vKey1 = 's:' + r.e.from;
      r.vKey2 = 't:' + r.e.to;
      addV(r.rs, r.vKey1, ys, yc);
      addV(r.rt - 1, r.vKey2, yc, yt);
      if (!hGroups.has(r.laneT)) hGroups.set(r.laneT, new Map());
      const g = hGroups.get(r.laneT);
      const a = Math.min(r.rs, r.rt - 1), b = Math.max(r.rs, r.rt - 1);
      r.hKey = 't:' + r.e.to;
      const cur = g.get(r.hKey);
      if (cur) { cur.min = Math.min(cur.min, a); cur.max = Math.max(cur.max, b); }
      else g.set(r.hKey, { min: a, max: b });
    }
  }
  const colorIntervals = (groupMap, gap) => {
    const items = [...groupMap.entries()].sort((a, b) => a[1].min - b[1].min || (a[0] < b[0] ? -1 : 1));
    const ends = [];
    const assign = new Map();
    for (const [key, iv] of items) {
      let t = ends.findIndex((end) => end < iv.min - gap);
      if (t === -1) { t = ends.length; ends.push(iv.max); } else ends[t] = iv.max;
      assign.set(key, t);
    }
    return { assign, count: ends.length };
  };
  // Kanäle mit beschrifteten direkten Kanten brauchen Platz für das Label
  const labelNeed = new Map();
  for (const r of routes) {
    r.text = labelText(r.e) || '';
    if (r.direct && r.text) labelNeed.set(r.rs, Math.max(labelNeed.get(r.rs) || 0, labelWidth(r.text) + 18));
  }
  const vTracks = new Map();
  for (const [ch, g] of vGroups) vTracks.set(ch, colorIntervals(g, 6));
  const hTracks = new Map();
  for (const [lane, g] of hGroups) hTracks.set(lane, colorIntervals(g, 0.01));

  // 5. Geometrie: Kanalbreiten, Spalten-x, Korridorhöhen, Bahn-y
  const chWidth = (ch) => {
    const t = vTracks.get(ch);
    if (ch === -1 && !t) return D.edgeChannel;
    return Math.max(labelNeed.get(ch) || 0, D.channelBase + Math.max(0, (t ? t.count : 0) - 1) * D.trackGap);
  };
  const colX = [];
  let x = D.laneHeader + chWidth(-1);
  for (let r = 0; r <= maxRank; r++) {
    colX.push(x);
    x += D.W + chWidth(r);
  }
  const width = x - chWidth(maxRank) + D.marginRight;
  const chStart = (ch) => (ch === -1 ? D.laneHeader : colX[ch] + D.W);
  const trackX = (ch, key) => {
    const t = vTracks.get(ch);
    const w = chWidth(ch);
    const n = t.count;
    const i = t.assign.get(key);
    return Math.round(chStart(ch) + w / 2 + (i - (n - 1) / 2) * D.trackGap);
  };
  const corr = lanes.map((l) => {
    const t = hTracks.get(l);
    return D.corridorBase + (t ? t.count : 0) * D.trackGap;
  });
  const Y = computeY(lanes, laneRows, corr);
  const trackY = (lane, key) => {
    const li = laneIdx.get(lane);
    const t = hTracks.get(lane);
    const top = Y.laneTop[li] + D.corridorBase / 2 + D.trackGap / 2;
    return Math.round(top + t.assign.get(key) * D.trackGap);
  };

  const nodePos = {};
  for (const n of sorted) {
    const li = laneIdx.get(n.lane);
    const r = rank.get(n.id);
    nodePos[n.id] = {
      x: colX[r],
      y: Y.laneTop[li] + corr[li] + rowOf.get(n.id) * (D.H + D.rowGap),
      w: D.W,
      h: D.H,
      rank: r,
      row: rowOf.get(n.id),
      lane: n.lane,
    };
  }

  const edgeOut = routes.map((r) => {
    const s = nodePos[r.e.from], t = nodePos[r.e.to];
    const ys = s.y + s.h / 2, yt = t.y + t.h / 2;
    const xs = s.x + s.w, xt = t.x;
    let pts, labelPos;
    if (r.direct) {
      const tx = trackX(r.rs, r.vKey1);
      pts = [[xs, ys], [tx, ys], [tx, yt], [xt, yt]];
      labelPos = ys === yt ? { x: (xs + xt) / 2, y: yt } : { x: tx, y: (ys + yt) / 2 };
    } else {
      const tx1 = trackX(r.rs, r.vKey1);
      const tx2 = trackX(r.rt - 1, r.vKey2);
      const yc = trackY(r.laneT, r.hKey);
      pts = [[xs, ys], [tx1, ys], [tx1, yc], [tx2, yc], [tx2, yt], [xt, yt]];
      labelPos = { x: (tx1 + tx2) / 2, y: yc };
    }
    return { id: r.e.id, from: r.e.from, to: r.e.to, typ: r.e.typ, label: r.e.label || '', back: r.back, points: simplify(pts), labelPos };
  });

  const laneOut = lanes.map((l, i) => ({ id: l, y: Y.laneTop[i], h: Y.laneH[i], rows: laneRows[i], corridor: corr[i] }));
  return {
    width,
    height: Y.total,
    lanes: laneOut,
    cols: colX.map((cx, r) => ({ x: cx, w: D.W, rank: r, chRight: chWidth(r) })),
    nodes: nodePos,
    edges: edgeOut,
    maxRank,
  };
}

function computeY(lanes, laneRows, corr) {
  const D = DIM;
  const laneTop = [], laneH = [];
  let y = 0;
  lanes.forEach((_, i) => {
    laneTop.push(y);
    const h = corr[i] + laneRows[i] * D.H + (laneRows[i] - 1) * D.rowGap + D.lanePad;
    laneH.push(h);
    y += h;
  });
  return { laneTop, laneH, corr, total: y };
}

function simplify(pts) {
  const res = [];
  for (const p of pts) {
    const last = res[res.length - 1];
    if (last && last[0] === p[0] && last[1] === p[1]) continue;
    res.push(p);
  }
  // kollineare Mittelpunkte entfernen
  for (let i = res.length - 2; i > 0; i--) {
    const [a, b, c] = [res[i - 1], res[i], res[i + 1]];
    if ((a[0] === b[0] && b[0] === c[0]) || (a[1] === b[1] && b[1] === c[1])) res.splice(i, 1);
  }
  return res;
}

/** Ermittelt aus einer Diagramm-Koordinate die Zielzelle für Drag & Drop. */
export function locate(layout, x, y) {
  const D = DIM;
  let lane = null;
  for (const l of layout.lanes) if (y >= l.y && y < l.y + l.h) lane = l;
  if (!lane) lane = y < 0 ? layout.lanes[0] : layout.lanes[layout.lanes.length - 1];
  let col = 0;
  for (const c of layout.cols) if (x >= c.x - c.chRight / 2 - 10) col = c.rank;
  if (layout.cols.length && x > layout.cols[layout.cols.length - 1].x + D.W + 40) col = layout.maxRank + 1;
  const inner = y - lane.y - lane.corridor;
  const row = Math.max(0, Math.round(inner / (D.H + D.rowGap)));
  return { lane: lane.id, col, row };
}
